import { type AudioFrame, SILENT_FRAME } from '../core/audio-frame.js';
import { extractBands } from './bands.js';
import { BeatDetector } from './beat.js';

export type AudioSourceKind = 'none' | 'file' | 'microphone';

const FFT_SIZE = 2048;
const ANALYSER_SMOOTHING = 0.8;

/**
 * Owns the Web Audio graph: source → analyser (→ speakers for file playback).
 * All analysis maths lives in `bands.ts` / `beat.ts` as pure functions;
 * this class only touches browser APIs.
 */
export class AudioEngine {
  private context: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private currentSource: AudioBufferSourceNode | MediaStreamAudioSourceNode | null = null;
  private currentStream: MediaStream | null = null;
  private readonly detector = new BeatDetector();
  private spectrum: Uint8Array<ArrayBuffer> = new Uint8Array(0);
  private kind: AudioSourceKind = 'none';
  private trackName = '';

  public get sourceKind(): AudioSourceKind {
    return this.kind;
  }

  public get currentTrackName(): string {
    return this.trackName;
  }

  /** Decode an audio file and play it on loop through the analyser. */
  public async playFile(file: File): Promise<void> {
    const { context, analyser } = await this.ensureContext();
    this.stopCurrent();

    const encoded = await file.arrayBuffer();
    const buffer = await context.decodeAudioData(encoded);

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(analyser);
    analyser.connect(context.destination);
    source.start();

    this.currentSource = source;
    this.kind = 'file';
    this.trackName = file.name;
  }

  /** Listen to the user's microphone (analysis only — mic is never routed to speakers). */
  public async useMicrophone(): Promise<void> {
    const { context, analyser } = await this.ensureContext();
    this.stopCurrent();

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);

    this.currentSource = source;
    this.currentStream = stream;
    this.kind = 'microphone';
    this.trackName = 'microphone';
  }

  /** Read the analyser and produce one immutable frame of audio features. */
  public sample(): AudioFrame {
    const analyser = this.analyser;
    const context = this.context;
    if (analyser === null || context === null || this.kind === 'none') {
      return SILENT_FRAME;
    }

    analyser.getByteFrequencyData(this.spectrum);
    const bands = extractBands(this.spectrum, context.sampleRate);
    const beat = this.detector.update(this.spectrum);
    return {
      level: bands.level,
      bass: bands.bass,
      mid: bands.mid,
      treble: bands.treble,
      beat: beat.isBeat,
      beatIntensity: beat.intensity,
    };
  }

  private async ensureContext(): Promise<{ context: AudioContext; analyser: AnalyserNode }> {
    this.context ??= new AudioContext();
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }
    if (this.analyser === null) {
      this.analyser = this.context.createAnalyser();
      this.analyser.fftSize = FFT_SIZE;
      this.analyser.smoothingTimeConstant = ANALYSER_SMOOTHING;
      this.spectrum = new Uint8Array(this.analyser.frequencyBinCount);
    }
    return { context: this.context, analyser: this.analyser };
  }

  private stopCurrent(): void {
    if (this.currentSource instanceof AudioBufferSourceNode) {
      this.currentSource.stop();
    }
    if (this.currentSource !== null) {
      this.currentSource.disconnect();
      this.currentSource = null;
    }
    if (this.currentStream !== null) {
      for (const track of this.currentStream.getTracks()) {
        track.stop();
      }
      this.currentStream = null;
    }
    this.kind = 'none';
    this.trackName = '';
  }
}
